import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Actions as IDX } from '../../../store/actions/indexationActions';
import Breadcrumb from '../../Common/Breadcrumb';
import { toggleAlertMessage } from '../../../store/action';

const AgreementIndexationEvents = ({ getContractIndexation, patchContractIndexationEvent, showMessage }) => {
  const { id: contractId } = useParams();
  const [overrideVal, setOverrideVal] = useState({});
  const [noteVal, setNoteVal] = useState({});
  const [data, setData] = useState(null);
  const [localLoading, setLocalLoading] = useState(!!contractId);

  useEffect(() => {
    if (!contractId) return;
    (async () => {
      setLocalLoading(true);
      const d = await getContractIndexation(contractId);
      setData(d);
      setLocalLoading(false);
    })();
  }, [contractId, getContractIndexation]);

  const events = (data && data.events) || [];

  const onPatch = async (ann, body) => {
    const r = await patchContractIndexationEvent(contractId, ann, body);
    if (r?.ok) {
      showMessage(true, 'Updated', 'success', 2000);
      const d = await getContractIndexation(contractId);
      setData(d);
    } else if (r?.code === 'event_already_applied') {
      showMessage(true, 'This period was already applied', 'error', 3000);
    } else {
      showMessage(true, 'Update failed', 'error', 3000);
    }
  };

  if (!contractId) {
    return <p>Missing contract id</p>;
  }

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <Breadcrumb
        items={[
          { label: 'Agreements', link: '/admin/agreements' },
          { label: 'Indexation' },
        ]}
      />
      <Card>
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle>Indexation — {contractId}</CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/indexation/rates">Rates table</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {localLoading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="border border-border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Anniversary</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>%</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                        No pending indexation events.
                      </TableCell>
                    </TableRow>
                  )}
                  {events.map((ev) => {
                    const ann = ev.anniversaryDateIso || ev.anniversary;
                    const st = ev.status;
                    return (
                      <TableRow key={String(ann)}>
                        <TableCell className="font-mono text-xs">{String(ann)}</TableCell>
                        <TableCell>
                          {st}
                          {st === 'applied' && ' (read-only)'}
                        </TableCell>
                        <TableCell>
                          {ev.computedPercent != null && <span>computed: {ev.computedPercent} </span>}
                        </TableCell>
                        <TableCell className="space-y-2 min-w-[200px]">
                          {st !== 'applied' && (
                            <>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => onPatch(ann, { status: 'skipped' })}
                                >
                                  Skip
                                </Button>
                                <div className="flex gap-1">
                                  <Input
                                    className="h-8 w-20"
                                    placeholder="override %"
                                    value={overrideVal[ann] || ''}
                                    onChange={(e) => setOverrideVal((o) => ({ ...o, [ann]: e.target.value }))}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      const p = overrideVal[ann];
                                      if (p) onPatch(ann, { overridePercent: Number(p) });
                                    }}
                                  >
                                    Save %
                                  </Button>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Note</Label>
                                <div className="flex gap-1">
                                  <Input
                                    className="h-8"
                                    value={noteVal[ann] != null && noteVal[ann] !== '' ? noteVal[ann] : ev.note || ''}
                                    onChange={(e) => setNoteVal((o) => ({ ...o, [ann]: e.target.value }))}
                                  />
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() =>
                                      onPatch(ann, { note: noteVal[ann] != null && noteVal[ann] !== '' ? noteVal[ann] : ev.note })
                                    }
                                  >
                                    Save note
                                  </Button>
                                </div>
                              </div>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const mapDispatch = (d) => ({
  getContractIndexation: (cid) => d(IDX.getContractIndexation(cid)),
  patchContractIndexationEvent: (cid, ann, b) => d(IDX.patchContractIndexationEvent(cid, ann, b)),
  showMessage: (...a) => d(toggleAlertMessage(...a)),
});

export default connect(null, mapDispatch)(AgreementIndexationEvents);
